import React, { useContext, useEffect, useState, memo } from 'react'
import { useSelector } from 'react-redux'
import { Linking, View } from 'react-native'
import Moment from 'moment-timezone'
import * as AddCalendarEvent from 'react-native-add-calendar-event'
import Alert from 'APP/Converse/Alert'

import { useShareDeepLinkFromConfig } from 'APP/Hooks/Share'
import Card from 'APP/Components/Chat/Card'
import TextMessage from 'APP/Components/Chat/TextMessage'
import Analytics from 'APP/Services/Analytics'
import I18n from 'APP/Services/i18n'
import { createIcsFile, parseIcsUrl } from 'APP/Helpers/IcsUtils'
import { isMobile, isWeb } from 'APP/Helpers/checkPlatform'
import { selectPreferredLanguage } from 'APP/Redux/PatientRedux'

import Config from 'APP/Config'
import { getBrandConfig } from 'APP/Lib/ConfigHelpers'
import { PostContext } from 'APP/Lib/Context'
import { Styles } from './style'
import { logDdError } from 'APP/Lib/Datadog'

import { checkCalendarState, requestCalendarPermission } from 'APP/NativeModules/PermissionsService'

const AddToCalendar = () => {
  const { post } = useContext(PostContext)
  const [permissions, setPermissions] = useState('')
  const { enableCalendarDeeplink } = useSelector((state) => state.features)
  const preferredLanguage = useSelector(selectPreferredLanguage)

  const episodeLink = useShareDeepLinkFromConfig(
    'ADD_TO_CALENDAR_EPISODE_DEEP_LINK_URL',
    { chat: post.channel_id },
    { analyticsName: 'upcomingAppointmentCard.addToCalendar.episodeLink' }
  )

  useEffect(() => {
    if (isMobile()) {
      checkCalendarState().then((res) => setPermissions(res))
    }
  }, [])

  const onPress = (choice) => {
    Analytics.trackEvent('button_click', { button_value: 'Add to calendar' })
    try {
      const onError = () => {
        Alert.alert(I18n.t('CannotAddToCalendar.title'), I18n.t('CannotAddToCalendar.body'), [
          { text: I18n.t('CannotAddToCalendar.ok') },
        ])
      }

      const { title, startDate, endDate, description } = parseIcsUrl(choice.link)
      // use patient preferred lang over device lang since the title and description are sent from BM
      const { productName } = getBrandConfig(Config.BRAND_ID, preferredLanguage)
      const brandedTitle = `${productName} - ${title}`
      const joinLink = I18n.t('Appointments.calendarJoinLink', {
        episodeLink: episodeLink.url,
        lng: preferredLanguage,
      })
      const notes = enableCalendarDeeplink ? `${joinLink}${description}` : description

      if (isWeb()) {
        const formattedStartDate = Moment.utc(startDate)
        const formattedEndDate = Moment.utc(endDate)
        createIcsFile(brandedTitle, notes, formattedStartDate, formattedEndDate)
          .then((result) => Linking.openURL(result))
          .catch(onError)
      } else {
        const eventConfig = {
          title: brandedTitle,
          startDate,
          endDate,
          notes,
        }
        if (permissions === 'granted') {
          AddCalendarEvent.presentEventCreatingDialog(eventConfig).catch(onError)
        } else {
          requestCalendarPermission().then(() => {
            // if null or denied, will prompt for permission
            // if blocked, will show error
            AddCalendarEvent.presentEventCreatingDialog(eventConfig).catch(onError)
          })
        }
      }
    } catch (err) {
      logDdError(err.message, err.stack)
    }
  }

  return (
    <View style={Styles.container}>
      <TextMessage post={post} isMember={false} />
      <View>
        {post.props.choices
          .filter((choice) => choice.link !== undefined)
          .map((choice) => {
            return (
              <Card
                key={choice.id}
                title={choice.display}
                iconName="calendar-blank"
                onPress={() => onPress(choice)}
                testID="CALENDAR_CARD"
              />
            )
          })}
      </View>
    </View>
  )
}

export default memo(AddToCalendar)
